import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, StickyNote } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

// Add / Edit an internal note via a popup (no inline editing).
export default function NoteModal({ open, onOpenChange, leadId, note, onSaved }) {
  const editing = !!note;
  const [content, setContent] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setContent(note?.content || ""); setReason(""); }
  }, [open, note]);

  const submit = async () => {
    const text = content.trim();
    if (!text) return toast.error("Note content is required");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/leads/${leadId}/notes/${note.id}`, { content: text, reason: reason.trim() || undefined });
        toast.success("Note updated");
      } else {
        await api.post(`/leads/${leadId}/notes`, { content: text });
        toast.success("Note added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="note-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><StickyNote className="h-5 w-5 text-loop" />{editing ? "Edit Internal Note" : "Add Internal Note"}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-ink/60">{editing ? "Update this note. The previous version is preserved in the note's history." : "Add a private note about this lead. Visible to your team."}</DialogDescription>
        <div className="space-y-3">
          <textarea
            data-testid="note-content-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
            autoFocus
            placeholder="e.g. Decision maker confirmed. Budget ₹2L, wants to start next month."
            className="w-full rounded-lg border border-sky p-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20"
          />
          {editing && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ink/60">Reason for edit (optional)</label>
              <input
                data-testid="note-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you changing this note?"
                className="h-10 w-full rounded-lg border border-sky px-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20"
              />
            </div>
          )}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} data-testid="note-cancel" className="rounded-lg border border-sky px-4 py-2 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="note-save" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Add note"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
